import dynamic from "next/dynamic";
import { FC, useEffect, useMemo, useState } from "react";
import { ArrowRightOutlineIcon } from "@smile2impress/components";
import { Box, Container } from "@smile2impress/components/relaunch";
import Head from "next/head";
import {
    MainBannerHeader,
    MainBannerHeaderProps,
} from "templates/relaunch/MainBanner/components";
import { TwoColumnList } from "templates/relaunch/TwoColumnList";

import { UrlsConfig } from "configs/urlsConfig";
import {
    generateJSONSchema,
    JSONSchemaServiceContentType,
} from "services/JSONSchemaService";

import { ClinicPageArticle } from "./components/ClinicPageArticle";
import { ClinicPageLayoutProps } from "./ClinicPageLayout.types";

const Map = dynamic(
    () =>
        import("./components/Map").then(({ Map }) => ({
            default: Map,
        })),
    { ssr: false }
);

export const ClinicPageLayout: FC<ClinicPageLayoutProps> = ({ content }) => {
    const [universalFormUrl, setUFUrl] = useState(
        `${UrlsConfig.UNIVERSAL_FORM}${
            content.clinic.city ? `?location=${content.clinic.city}` : ""
        }`
    );

    const mainBannerHeaderContent: MainBannerHeaderProps = {
        title: content?.title || "",
        subtitle: content?.subtitle || "",
        text: content?.description,
        headingTextStyle: "H2",
        cta: {
            text: content?.sidebar.button.name || "",
            link: universalFormUrl,
            iconRight: ArrowRightOutlineIcon,
        },
        headingAs: "h1",
    };

    const twoColumnContent = {
        title: content?.howToGet?.title || "",
        withMobileList: true,
        items: [
            {
                icon: "PublicTransportIcon",
                title: content?.howToGet?.publicTransport?.title || "",
                text: content?.howToGet?.publicTransport?.description || "",
                isCmsContent: true,
            },
            {
                icon: "ParkingIcon",
                title: content?.howToGet?.parking?.title || "",
                text: content?.howToGet?.parking?.description || "",
                isCmsContent: true,
            },
        ],
        singleColumnOnMobile: true,
    };
    const clinicData = useMemo(
        () => ({
            baseData: content?.clinic,
            additionalData: {
                ...content?.sidebar,
                clinicPhotos: content?.photos,
                cta: {
                    label: content?.ctaButtonLabel,
                    link: content?.ctaButtonLink,
                },
            },
        }),
        [content]
    );

    useEffect(() => {
        const idFromUrl = new URLSearchParams(window.location.search).get(
            "clinicId"
        );
        if (idFromUrl) {
            setUFUrl((prev) => {
                const withQuery = prev.indexOf("?");
                return `${prev}${withQuery ? "&" : "?"}clinicId=${idFromUrl}`;
            });
        }
    }, []);

    const schema = generateJSONSchema({
        type: JSONSchemaServiceContentType.CLINIC,
        content: {
            clinicEntity: content.clinic,
            ...(content.photos.length > 0 && {
                image: content.photos[0],
            }),
        },
    });

    return (
        <>
            <Head>
                {schema && (
                    <script
                        key="JSONSchema"
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(schema).replace(
                                /</g,
                                "\\u003c"
                            ),
                        }}
                    />
                )}
                ;
            </Head>

            <Box>
                <Container mb={[3, 3, 4]}>
                    <MainBannerHeader {...mainBannerHeaderContent} />
                </Container>
                <Container variant="noMobilePX" mb={4}>
                    <Map clinic={clinicData} />
                </Container>
                <Container mb={[4, 4, 5]}>
                    <TwoColumnList content={twoColumnContent} />
                </Container>
                {content?.article?.content && (
                    <Container mb={[4, 4, 5]}>
                        <ClinicPageArticle content={content.article} />
                    </Container>
                )}
            </Box>
        </>
    );
};
